/**
 * Matching what the agent asked for against the values a screen accepts.
 *
 * The agent does not know a page's internal ids. It says "Risk", "risk_stress",
 * "quarterly" or "Q", and it copies labels as often as ids. So matching is
 * forgiving about spelling — case, spaces, underscores and hyphens are ignored,
 * and a label is as good as an id — but strict about MEANING. Ki_UI also
 * accepted a loose contains match in both directions, which is how "overall"
 * selected the "ALL" tab and "1" selected "1W": a confident wrong answer is
 * worse than a refusal the agent can correct. Here a value is an exact id, an
 * exact label, or an unambiguous prefix, and nothing else.
 *
 * Pure: no React, no DOM, no store — importable from Node check scripts.
 */

/** The spelling-insensitive form two option strings are compared in. */
export const normOption = (s) => String(s ?? '').toLowerCase().replace(/[\s_-]/g, '');

/** Shortest prefix accepted; one character names nothing with confidence. */
const MIN_PREFIX = 2;

const isPlainObject = (v) =>
  v !== null && typeof v === 'object' && !Array.isArray(v)
  && (Object.getPrototypeOf(v) === Object.prototype || Object.getPrototypeOf(v) === null);

/** `{ id, label }`, `{ key, label }`, `{ value, label }` or a bare string. */
function optionId(opt) {
  if (typeof opt === 'string' || typeof opt === 'number') return opt;
  if (!isPlainObject(opt)) return null;
  const id = opt.id ?? opt.key ?? opt.value;
  return typeof id === 'string' || typeof id === 'number' ? id : null;
}

const optionLabel = (opt) => (isPlainObject(opt) && typeof opt.label === 'string' ? opt.label : null);

/** The single id every match agrees on, or null when there is none or several. */
function unique(matches) {
  const ids = [...new Set(matches)];
  return ids.length === 1 ? ids[0] : null;
}

/**
 * The option id `raw` names, or null.
 *
 * In order, and the first stage that matches decides:
 *  1. an id equal to `raw` as written, then equal after normalising;
 *  2. a label equal after normalising;
 *  3. an id or label that STARTS WITH `raw` — at least two characters, not a
 *     bare number (so "1" never becomes "1w" and "12" never becomes "12m"),
 *     and only when exactly one option does.
 * A stage that matches two different options is ambiguous and answers null
 * rather than guessing. There is no contains match in either direction.
 */
export function resolveOption(raw, options) {
  if (!Array.isArray(options) || !options.length) return null;
  let value = raw;
  if (isPlainObject(value)) value = optionId(value) ?? value.label;
  if (value === null || value === undefined || typeof value === 'object' || typeof value === 'function') return null;

  const n = normOption(value);
  if (!n) return null;

  const entries = options
    .map((opt) => ({ id: optionId(opt), label: optionLabel(opt) }))
    .filter((o) => o.id !== null && o.id !== '');

  const verbatim = entries.find((o) => String(o.id) === String(value));
  if (verbatim) return verbatim.id;

  const byId = unique(entries.filter((o) => normOption(o.id) === n).map((o) => o.id));
  if (byId !== null) return byId;
  if (entries.some((o) => normOption(o.id) === n)) return null;

  const labelled = entries.filter((o) => o.label && normOption(o.label) === n);
  if (labelled.length) return unique(labelled.map((o) => o.id));

  if (n.length < MIN_PREFIX || /^\d+$/.test(n)) return null;
  const prefixed = entries.filter(
    (o) => normOption(o.id).startsWith(n) || (o.label && normOption(o.label).startsWith(n)),
  );
  return prefixed.length ? unique(prefixed.map((o) => o.id)) : null;
}
